import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { IMPROVEMENT_OPTIONS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", company: "", email: "", phone: "",
  warehouse_size: "", number_of_sites: "", current_wms: "",
  improvement_area: "", message: "",
};

export const DemoForm = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email) {
      toast.error("Please fill in your name, company and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo`, form);
      setDone(true);
      toast.success("Demo request received — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => { setForm(EMPTY); setDone(false); }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="demo-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0B1240]/60 p-4 backdrop-blur-sm"
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-8 shadow-2xl lg:p-10"
          >
            <button data-testid="demo-close" onClick={close} className="absolute right-5 top-5 text-slate-400 transition-colors hover:text-slate-900">
              <X className="h-6 w-6" />
            </button>

            {done ? (
              <div className="flex flex-col items-center py-14 text-center">
                <CheckCircle2 className="h-16 w-16 text-volt" strokeWidth={1.4} />
                <h3 className="mt-6 font-display text-3xl font-extrabold text-slate-900">Request received.</h3>
                <p className="mt-3 max-w-sm text-slate-500">
                  Thanks {form.name.split(" ")[0] || "there"} — our team will reach out to book your
                  Stockaris walkthrough. Keep an eye on your inbox.
                </p>
                <button data-testid="demo-done" onClick={close} className="mt-8 bg-volt px-7 py-3 font-display font-bold text-white transition-transform hover:-translate-y-0.5">
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="font-mono-p text-xs uppercase tracking-[0.25em] text-volt">Book a Stockaris Demo</div>
                <h3 className="mt-3 font-display text-3xl font-extrabold leading-tight text-slate-900 sm:text-4xl">
                  Ready to take control?
                </h3>
                <p className="mt-2 text-sm text-slate-400">Tell us about your operation and we'll tailor the walkthrough.</p>

                <form onSubmit={submit} className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Full name *"><Input data-testid="demo-name" value={form.name} onChange={set("name")} placeholder="Jane Smith" className="border-slate-200 bg-white" /></Field>
                  <Field label="Company *"><Input data-testid="demo-company" value={form.company} onChange={set("company")} placeholder="Acme Logistics" className="border-slate-200 bg-white" /></Field>
                  <Field label="Work email *"><Input data-testid="demo-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@acme.com" className="border-slate-200 bg-white" /></Field>
                  <Field label="Phone"><Input data-testid="demo-phone" value={form.phone} onChange={set("phone")} placeholder="+61 400 000 000" className="border-slate-200 bg-white" /></Field>
                  <Field label="Warehouse size"><Input data-testid="demo-size" value={form.warehouse_size} onChange={set("warehouse_size")} placeholder="e.g. 8,000 m²" className="border-slate-200 bg-white" /></Field>
                  <Field label="Number of sites"><Input data-testid="demo-sites" value={form.number_of_sites} onChange={set("number_of_sites")} placeholder="e.g. 3" className="border-slate-200 bg-white" /></Field>
                  <Field label="Current WMS"><Input data-testid="demo-current-wms" value={form.current_wms} onChange={set("current_wms")} placeholder="e.g. Spreadsheets / SAP / none" className="border-slate-200 bg-white" /></Field>
                  <Field label="Looking to improve">
                    <Select value={form.improvement_area} onValueChange={(v) => setForm((f) => ({ ...f, improvement_area: v }))}>
                      <SelectTrigger data-testid="demo-improvement" className="border-slate-200 bg-white"><SelectValue placeholder="Select area" /></SelectTrigger>
                      <SelectContent>
                        {IMPROVEMENT_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o} data-testid={`improve-${o.toLowerCase().replace(/\s+/g, "-")}`}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Anything else?">
                      <Textarea data-testid="demo-message" value={form.message} onChange={set("message")} placeholder="Tell us about your biggest warehouse headache…" className="min-h-[90px] border-slate-200 bg-white" />
                    </Field>
                  </div>

                  <button
                    data-testid="demo-submit"
                    type="submit"
                    disabled={loading}
                    className="group mt-2 flex items-center justify-center gap-2 bg-volt py-4 font-display text-base font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60 sm:col-span-2"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Book my Demo <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.2} /></>}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-2">
    <span className="font-mono-p text-[11px] uppercase tracking-wider text-slate-500">{label}</span>
    {children}
  </label>
);
